import React, { useEffect, useState } from 'react';
import { TranslationView } from '@levelapp/softfabric-ui';
import { Image, Linking, Text, View } from 'react-native';
import Helpers from '../../../../../Common/Helpers/AmountHelpers';
import Project from "../../../../../Common/DTO/Project";
import { styles } from "./LeaderViewStyle";
import { getImageAspectRatio } from "../../../../../Common/Utils/image.utils";
import { useWindowSizeContext } from "../../../../../Common/contexts/WindowSizeContext";
import { ProjectOwner } from "../../../../../Common/DTO/ProjectOwner";
import { Application, LanguageManager, Managers } from "@levelapp/softfabric";

type TranslationLanguageKey = "fr" | "nl" | "en";

interface LeaderViewProps {
    project: Project;
    organigramImgUrl: string;
    projectOwners: ProjectOwner[];
}

interface ImageSize {
    width: number;
    height: number;
}

export const LeaderView = ({ project, organigramImgUrl, projectOwners }: LeaderViewProps) => {
    const { width: screenWidth, isMobile } = useWindowSizeContext();

    const projectLeaderArray = project.projectPropertyValues.filter(x => x.projectPropertyKey.projectPropertyType.key === "PROJECT_LEADER_ARRAY");

    const [organigramAspectRatio, setOrganigramAspectRatio] = useState(0);

    const [organigramSize, setOrganigramSize] = useState<ImageSize>({ width: 0, height: 0 });
    const [ownerSize, setOwnersSize] = useState<ImageSize>();
    const languageManager: LanguageManager = Application.current.resolve<LanguageManager>(Managers.LANGUAGE_MANAGER);
    const language = languageManager.language.slice(0, 2);

    const maxWidthLeader = isMobile ? screenWidth * 0.75 : screenWidth * 0.25;
    const maxWidthOrganigram = isMobile ? screenWidth * 0.75 : screenWidth * 0.5

    function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            Image.getSize(
                url,
                (width, height) => resolve({ width, height }),
                (error) => reject(error)
            );
        });
    }

    useEffect(() => {
        if (organigramImgUrl.length === 0 && projectOwners.length === 0) return;

        const ownerSizesTmp: ImageSize[] = [];

        (async () => {
            try {
                if (organigramImgUrl) {
                    const organigramImgDimensions = await getImageDimensions(organigramImgUrl);
                    const organigramImgRatio = await getImageAspectRatio(organigramImgUrl);

                    const resizeOrganigramImgHeight = maxWidthOrganigram / organigramImgRatio;

                    if (organigramImgDimensions.width < maxWidthOrganigram || organigramImgDimensions.height < resizeOrganigramImgHeight) {
                        setOrganigramSize({
                            width: organigramImgDimensions.width,
                            height: organigramImgDimensions.height
                        });
                    } else {
                        setOrganigramSize({ width: maxWidthOrganigram, height: maxWidthOrganigram / organigramImgRatio });
                    }

                    setOrganigramAspectRatio(organigramImgRatio);
                }

                for (const projectOwner of projectOwners) {
                    const ownerImgDimensions = await getImageDimensions(projectOwner.imageUrl);
                    const ownerImgRatio = await getImageAspectRatio(projectOwner.imageUrl);
                    const resizeOwnerImgHeight = maxWidthLeader / ownerImgRatio;

                    if (ownerImgDimensions.width < maxWidthLeader || ownerImgDimensions.height < resizeOwnerImgHeight) {
                        ownerSizesTmp.push({ width: ownerImgDimensions.width, height: ownerImgDimensions.height });
                    } else {
                        ownerSizesTmp.push({ width: maxWidthLeader, height: resizeOwnerImgHeight });
                    }
                }

                let minSize: ImageSize = ownerSizesTmp[0];

                for (let i = 1; i < ownerSizesTmp.length; i++) {
                    if (ownerSizesTmp[i].width < minSize.width) minSize = ownerSizesTmp[i];
                }

                setOwnersSize(minSize);

            } catch (error) {
                console.error("Error fetching image aspect ratio:", error);
            }
        })();

    }, [organigramAspectRatio, screenWidth, maxWidthLeader, maxWidthOrganigram, organigramImgUrl, projectOwners]);

    const ProjectDescription = ({ description }: { description: string }) => {
        const links = [
            { phrase: 'En savoir plus sur Mangouste Anim', url: 'https://mangousteanim.com/' },
            { phrase: 'Meer informatie over Mangouste Anim', url: 'https://mangousteanim.com/' },
            { phrase: 'TMI Group SAS', url: 'https://www.tmi-immo.com/' },
        ];

        // Créer un pattern regex avec toutes les phrases
        const pattern = new RegExp(`(${links.map(link =>
            link.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        ).join('|')})`, 'g');

        // Vérifier s'il y a des correspondances
        if (!pattern.test(description)) {
            return <Text style={styles.text}>{description}</Text>;
        }

        // Diviser le texte et créer les éléments
        const parts = description.split(pattern);

        return (
            <Text style={styles.text}>
                {parts.map((part, index) => {
                    const matchedLink = links.find(link => link.phrase === part);

                    if (matchedLink) {
                        return (
                            <Text
                                key={index}
                                style={{ color: '#007bff', textDecorationLine: 'underline' }}
                                onPress={() => Linking.openURL(matchedLink.url)}
                            >
                                {part}
                            </Text>
                        );
                    }

                    return part;
                })}
            </Text>
        );
    };
    const leaderDescription = Helpers.getPropertyValue(
        "LEADER_DESCRIPTION",
        project.projectPropertyValues
    );

    return (
        <View style={styles.page}>
            <TranslationView style={styles.title}>{projectOwners.length > 1 ? "leaders" : "leader"}</TranslationView>
            {(organigramImgUrl.length > 0) && (
                <>
                    <TranslationView style={styles.subTitle}>leader.organigram</TranslationView>
                    <Image style={[styles.organigram, organigramSize]} source={{ uri: organigramImgUrl }} />
                </>
            )}
            <ProjectDescription description={leaderDescription} />
            {project.code == "gold" &&
                <Image style={styles.logo} resizeMode="contain" source={{ uri: "https://beebondsstorage.blob.core.windows.net/files/gold/pictures/Mangouste.png" }} />
            }
            {projectOwners.length > 0 && leaderDescription != "" &&
                <TranslationView style={styles.subTitle}>{projectOwners.length > 1 ? "leaders" : "leader"}</TranslationView>
            }
            <View style={[isMobile ? styles.leadersContainerMobile : styles.leadersContainer]}>
                {projectOwners.map((projectOwner: ProjectOwner, index: number) => {
                    return (
                        <View key={"owner-in-" + index}
                            style={[isMobile ? styles.leaderElementMobile : styles.leaderElement]}>
                            <Image style={[styles.leader, ownerSize]} source={{ uri: projectOwner.imageUrl }} />
                            <Text style={styles.subSubTitle}>{projectOwner.fullName}</Text>
                            <Text
                                style={styles.text}>{projectOwner.biography[language as TranslationLanguageKey]}</Text>
                            {/* <Text
                                style={styles.text}>{projectOwner.description[language as TranslationLanguageKey]}</Text> */}
                        </View>
                    );
                })}
            </View>

            {projectLeaderArray.length > 0 && projectOwners.length > 0 && projectLeaderArray.some((property: any) => Helpers.getPropertyValue(property.projectPropertyKey.key, project.projectPropertyValues)) && (
                <View>
                    <View style={[styles.table]}>
                        <View style={styles.tableHeader}>
                            <Text style={styles.tableTitle}>
                                {Helpers.getPropertyValue("LEADER_NAME", project.projectPropertyValues)}
                            </Text>
                        </View>
                        {projectLeaderArray.map((property: any, index: number) => {
                            const value = Helpers.getPropertyValue(property.projectPropertyKey.key, project.projectPropertyValues);
                            if (!value) return null;

                            const isLastIndex = index === projectLeaderArray.length - 1;
                            return (
                                <View key={index} style={[styles.row]}>
                                    <View style={[styles.borderView, isLastIndex && { borderBottomLeftRadius: 24 }]}>
                                        <Text style={styles.arrayKey}>{Helpers.getCorrectTranslation(property.projectPropertyKey.title)}</Text>
                                    </View>
                                    <View style={[styles.borderView, isLastIndex && { borderBottomRightRadius: 24 }]}>
                                        <Text style={styles.arrayValue}>{value}</Text>
                                    </View>
                                </View>
                            );
                        })}
                    </View>
                </View>
            )}
            <br />
            <br />
            <br />
        </View>
    );
}

export default LeaderView;